'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { memberListMachine } from './machines/memberListMachine'
import type { CrmMember } from './types'

export type MemberListMachineApi = ReturnType<typeof useMachine<typeof memberListMachine>>

const MemberListMachineContext = createContext<MemberListMachineApi | null>(null)

export function MemberListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(memberListMachine)
  return (
    <MemberListMachineContext.Provider value={api}>{children}</MemberListMachineContext.Provider>
  )
}

export function useMemberListMachine() {
  const ctx = useContext(MemberListMachineContext)
  if (!ctx) {
    throw new Error('useMemberListMachine must be used within MemberListMachineProvider')
  }
  return ctx
}

export function useMemberListMachineOptional() {
  return useContext(MemberListMachineContext)
}

export function useMemberActions() {
  const machineApi = useMemberListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setMembers: (members: CrmMember[]) => send({ type: 'SET_MEMBERS', members }),
    addMember: (member: CrmMember) => send({ type: 'ADD_MEMBER', member }),
    updateMember: (id: string, updates: Partial<CrmMember>) =>
      send({ type: 'UPDATE_MEMBER', id, updates }),
    removeMember: (id: string) => send({ type: 'REMOVE_MEMBER', id }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useMemberListStoreCompat() {
  const machineApi = useMemberListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const members = state.context.members
    const isLoading = state.context.isLoading

    const getMemberById = (id: string) => members.find((m) => m.id === id)
    const getMembersByQueue = (queueId: string) =>
      members.filter((m) => m.queues?.includes(queueId))
    const getOnlineMembers = () =>
      members.filter(
        (m) => m.status === 'available' || m.status === 'onCall' || m.status === 'onChat'
      )

    return {
      members,
      isLoading,
      setMembers: (m: CrmMember[]) => send({ type: 'SET_MEMBERS', members: m }),
      addMember: (member: CrmMember) => send({ type: 'ADD_MEMBER', member }),
      updateMember: (id: string, updates: Partial<CrmMember>) =>
        send({ type: 'UPDATE_MEMBER', id, updates }),
      removeMember: (id: string) => send({ type: 'REMOVE_MEMBER', id }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      getMemberById,
      getMembersByQueue,
      getOnlineMembers,
    }
  }, [machineApi])
}
